<template>
    <div id="item-panel-tabs"
         data-component="ItemPanelTabs">
        <a v-for="tab in tabsWithItems"
           :key="tab.itemType"
           :class="{
               active: activeItemTypeTab === tab.itemType,
               disabled: !tabsWithFilteredItems.includes(tab)
           }"
           @click.prevent="setActiveTab(tab.itemType, true)">
            <i :class="tab.icon"
               class="fa-solid mobile-only"></i>
            <span class="desktop-only">{{ l(tab.labelLangKey) }}</span>
            <span v-if="searchQuery && filteredItemListCollections[tab.itemType] && filteredItemListCollections[tab.itemType].count"
                  class="label label-default">{{ filteredItemListCollections[tab.itemType] && filteredItemListCollections[tab.itemType].count }}</span>
        </a>
    </div>
</template>

<script>
import EventNames from '@/lib/Events/EventNames';
import { container } from '@/container';
import { getSellableItemTypeTabs } from '@/lib/Items/itemTypeTabs';
import { mapState } from 'vuex';

export default {
    name: 'ItemPanelTabs',

    props: {
        itemListCollections: {
            type: Object,
            required: true,
        },

        filteredItemListCollections: {
            type: Object,
            default: null,
        },

        searchQuery: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            /**
             * The order, icon and lang key here come from the shared itemTypeTabs config.
             * This means that the these tabs are always in sync with those on the Redeem Screen.
             *
             * @type {{itemType: string, icon: string, labelLangKey: string}[]}
             */
            allTabs: getSellableItemTypeTabs(),

            /**
             * @type {?SellState}
             */
            currentCartSellState: null,

            /**
             * Filtered list of tabs, containing only the tabs that contain any items.
             *
             * @type {{itemType: string, icon: string, labelLangKey: string}[]}
             */
            tabsWithItems: [],

            /**
             * We want to know if the user has manually clicked on a tab to select one,
             * but only if they did that for the cart that is currently shown - if the cart
             * changes (e.g. if an order is held / the clear button is pressed) it's okay
             * to reset the selected tab. So the value of this is the thirdPartyRef of the cart
             * that was active when the tab was pressed.
             *
             * @type {string|null}
             */
            userChangedTabForCart: null,
        };
    },

    computed: {
        ...mapState('cart', ['currentCartThirdPartyRef']),

        ...mapState('sell', [
            'activeItemTypeTab',
        ]),

        tabsWithFilteredItems() {
            if (!this.searchQuery) {
                return this.tabsWithItems;
            }

            return this.tabsWithItems.filter((tab) => {
                return this.filteredItemListCollections[tab.itemType]
                    && this.filteredItemListCollections[tab.itemType].count > 0;
            });
        },
    },

    watch: {
        '$store.state.cart.modified': {
            handler() {
                // Update the currentCartSellState when the cart is modified.
                this.currentCartSellState = container.cartService.currentCart.sellState;
            },
            immediate: true,
        },

        /**
         * SelState is held within the current cart and contains the configuration for how the sell page should
         * behave, including which item tabs are available and which is selected by default.
         *
         * The SellState should only change when the cart changes.
         *
         * To keep a long explanation short, this component is created faster than SellCtrl starts up and updates
         * the current cart / sell state to the correct value. So we need to watch for changes to sell state and
         * react accordingly.
         */
        'currentCartSellState': {
            handler() {
                this.updateAvailableTabs();
                this.selectDefaultTab();
            },
            immediate: true,
        },
    },

    created() {
        // After an item sync is finished make sure an appropriate tab is still selected.
        // An item sync always happens after changing the item branch.
        //
        // We need to remove the listener when this component is unmounted. $on returns
        // a function to do this, we'll store that and call it in beforeUnmount.
        this.deregisterItemsSyncedListenerFunc = container.$rootScope.$on(EventNames.ITEMS_SYNCED, () => {
            this.updateAvailableTabs();
            this.selectDefaultTab();
        });
    },

    beforeUnmount() {
        if (this.deregisterItemsSyncedListenerFunc) {
            this.deregisterItemsSyncedListenerFunc();
        }
    },

    methods: {
        getAvailableTab(itemType) {
            return this.tabsWithItems.find((t) => t.itemType === itemType);
        },

        /**
         * Update the available tabs property.
         * This is a method instead of using a computed property because we need to react to changes in
         * saleableItemService, which is outside of Vue and doesn't play nice with reactivity.
         */
        updateAvailableTabs() {
            const restrictItemTypes = this.currentCartSellState.restrictItemTypes;

            this.tabsWithItems = this.allTabs.filter((tab) => {
                // Hide the tab if there are restricted item types and this isn't one of them.
                if (restrictItemTypes !== null && restrictItemTypes.indexOf(tab.itemType) === -1) {
                    return false;
                }

                // Hide the table if there are no items of this type (regardless of search query).
                if (!this.itemListCollections[tab.itemType] || !this.itemListCollections[tab.itemType].count) {
                    return false;
                }

                // Show the tab.
                return true;
            });
        },

        selectDefaultTab() {
            // If the current sell state has a default tab switch to that one.
            if (this.currentCartSellState.defaultItemTypeTab !== null) {
                this.setActiveTab(this.currentCartSellState.defaultItemTypeTab);
                return;
            }

            // If the previously selected tab is still available, stay on that one.
            if (this.activeItemTypeTab && this.getAvailableTab(this.activeItemTypeTab)) {
                return;
            }

            // If the device's landing page is "[item type] tab on Sell screen" select that tab by default if available.
            if (
                container.currentDevice.device.landingPage
                && container.currentDevice.device.landingPage.top === 'sell'
                && container.currentDevice.device.landingPage.secondaryItemType
            ) {
                if (this.getAvailableTab(container.currentDevice.device.landingPage.secondaryItemType)) {
                    // The desired "[item type] tab on Sell screen" tab is available. Switch to that.
                    this.setActiveTab(container.currentDevice.device.landingPage.secondaryItemType);
                    return;
                }
            }

            if (this.tabsWithItems.length > 0) {
                this.setActiveTab(this.tabsWithItems[0].itemType);
                return;
            }
            this.setActiveTab(null);
        },

        /**
         * @param {string|null} itemType
         * @param {boolean} [byUser]
         */
        setActiveTab(itemType, byUser = false) {
            this.$store.commit('sell/setActiveItemTypeTab', itemType);

            if (byUser) {
                this.userChangedTabForCart = this.currentCartThirdPartyRef;
            }

            if (!itemType) {
                this.userChangedTabForCart = null;
            }
        },

        selectNextTab() {
            const currentTab = this.tabsWithItems.find((t) => t.itemType === this.activeItemTypeTab);
            const currentIndex = this.tabsWithItems.indexOf(currentTab);
            let nextIndex = currentIndex + 1;
            if (nextIndex >= this.tabsWithItems.length) {
                nextIndex = 0;
            }
            this.setActiveTab(this.tabsWithItems[nextIndex].itemType);
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";

#item-panel-tabs {
    flex-grow: 0;
    flex-shrink: 0;
    display: flex;
    margin: 0;
    padding: 0 3px;

    a {
        display: block;
        font-weight: bold;
        font-size: @font-size-medium;
        margin: 0 3px;
        padding: 15px 4px;
        white-space: nowrap;
        background: @dt-dark-blue;
        color: @white;
        text-align: center;
        cursor: pointer;
        border-radius: 5px 5px 0 0;
        flex-basis: 10%;
        flex-grow: 1;
        flex-shrink: 1;
        transition: all 0.1s ease-out;

        &.disabled {
            opacity: 0.4;
        }

        &.active {
            opacity: 1;
            background: @dt-mid-blue;
            color: @white;
        }

        // Number of search results for this item type.
        .label {
            margin-left: 5px;
            position: relative;
            top: -1px
        }
    }
}

@media (max-width: @mobile-max) {
    #item-panel-tabs {
        padding: 2px 2px 0;

        a {
            height: 45px;
            margin: 0 1px;
            font-size: 22px;
            padding: 7px 0;
        }
    }
}
</style>
